import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUserPlus, FaTrash, FaSpinner, FaEye, FaEyeSlash, FaUserCog, FaKey } from 'react-icons/fa';
import { databaseURL } from '../firebase';

const ManageAccess = () => {
  const [adminId, setAdminId] = useState('');
  const [adminPass, setAdminPass] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [adminList, setAdminList] = useState([]);
  const [showPassword, setShowPassword] = useState({});
  const [activeTab, setActiveTab] = useState('create');

  // Glass morphism style
  const glassStyle = {
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(10px)',
    borderRadius: '20px',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)'
  };

  const fetchAdmins = async () => {
    try {
      const response = await fetch(
        `${databaseURL}/adL/admins.json`
      );
      const data = await response.json();
      if (data) {
        const adminsArray = Object.entries(data).map(([key, value]) => ({
          key,
          ...value,
        }));
        setAdminList(adminsArray);
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
    }
  };

  useEffect(() => { fetchAdmins(); }, []);

  const handleCreateAdmin = async () => {
    if (!adminId || !adminPass) return alert('Please enter both ID and password');
    setIsSubmitting(true);
    try {
      await fetch(`${databaseURL}/adL/admins.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: adminId, pass: adminPass }),
      });
      setAdminId(''); setAdminPass(''); fetchAdmins();
    } catch (error) {
      console.error('Error creating admin:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAdmin = async (key) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await fetch(`${databaseURL}/adL/admins/${key}.json`,
        { method: 'DELETE' });
      fetchAdmins();
    } catch (error) {
      console.error('Error deleting admin:', error);
    }
  };

  const togglePasswordVisibility = (key) => {
    setShowPassword(prev => ({ ...prev, [key]: !prev[key] }));
  };

 return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', // Soft blue gradient
        padding: '2rem',
        fontFamily: "'Poppins', sans-serif",
        color: '#4a5568' // Grayish-blue text
      }}
    >
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <motion.div
          initial={{ y: -50 }}
          animate={{ y: 0 }}
          transition={{ type: "spring", stiffness: 100 }}
          style={{
            textAlign: 'center',
            marginBottom: '3rem',
            padding: '1.5rem',
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            border: '1px solid rgba(255, 255, 255, 0.9)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)'
          }}
        >
          <motion.div 
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1rem',
              marginBottom: '1rem'
            }}
          >
            <FaUserCog style={{ fontSize: '2.5rem', color: '#667eea' }} />
            <h1 style={{
              fontSize: '2.2rem',
              fontWeight: '700',
              margin: 0,
              background: 'linear-gradient(to right, #667eea, #764ba2)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Admin Portal
            </h1>
          </motion.div>
          <p style={{ 
            margin: 0,
            color: '#718096',
            fontSize: '1.1rem'
          }}>
            Manage administrator access and permissions
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '2rem',
            gap: '1rem'
          }}
        >
          {['create', 'view'].map((tab) => (
            <motion.button
              key={tab}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '0.8rem 2rem',
                borderRadius: '50px',
                border: 'none',
                background: activeTab === tab ? '#667eea' : 'rgba(102, 126, 234, 0.1)',
                color: activeTab === tab ? 'white' : '#667eea',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textTransform: 'capitalize'
              }}
            >
              {tab === 'create' ? (
                <><FaUserPlus style={{ marginRight: '0.5rem' }} /> Create Admin</>
              ) : (
                <><FaKey style={{ marginRight: '0.5rem' }} /> View Admins</>
              )}
            </motion.button>
          ))}
        </motion.div>

        {/* Content Area */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '2rem'
        }}>
          {/* Create Admin Form */}
          <AnimatePresence>
            {activeTab === 'create' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                style={{
                  padding: '2rem',
                  background: 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  border: '1px solid rgba(255, 255, 255, 0.9)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)'
                }}
              >
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  marginBottom: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.8rem',
                  color: '#4a5568'
                }}>
                  <FaUserPlus style={{ color: '#667eea' }} /> Create New Admin
                </h2>
                
                <div style={{
                  display: 'grid',
                  gap: '1.5rem'
                }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: '#4a5568'
                    }}>Admin ID</label>
                    <motion.input
                      whileFocus={{ 
                        scale: 1.02,
                        boxShadow: '0 0 0 2px rgba(102, 126, 234, 0.3)'
                      }}
                      type="text"
                      value={adminId}
                      onChange={(e) => setAdminId(e.target.value)}
                      placeholder="Enter admin ID"
                      style={{
                        width: '100%',
                        padding: '0.8rem 1rem',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        background: 'rgba(255, 255, 255, 0.8)',
                        color: '#4a5568',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'all 0.3s ease'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: '#4a5568'
                    }}>Password</label>
                    <motion.input
                      whileFocus={{ 
                        scale: 1.02,
                        boxShadow: '0 0 0 2px rgba(102, 126, 234, 0.3)'
                      }}
                      type="password"
                      value={adminPass}
                      onChange={(e) => setAdminPass(e.target.value)}
                      placeholder="Enter password"
                      style={{
                        width: '100%',
                        padding: '0.8rem 1rem',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        background: 'rgba(255, 255, 255, 0.8)',
                        color: '#4a5568',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'all 0.3s ease'
                      }}
                    />
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleCreateAdmin}
                    disabled={isSubmitting}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      borderRadius: '12px',
                      background: 'linear-gradient(to right, #667eea, #764ba2)',
                      color: '#fff',
                      border: 'none',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      marginTop: '1rem',
                      opacity: isSubmitting ? 0.7 : 1,
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <motion.span
                          animate={{ rotate: 360 }}
                          transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                        >
                          <FaSpinner />
                        </motion.span>
                        Creating...
                      </>
                    ) : (
                      <>
                        <FaUserPlus /> Create Admin
                      </>
                    )}
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Admin List */}
          <AnimatePresence>
            {activeTab === 'view' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                style={{
                  padding: '2rem',
                  background: 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  border: '1px solid rgba(255, 255, 255, 0.9)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)'
                }}
              >
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  marginBottom: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.8rem',
                  color: '#4a5568'
                }}>
                  <FaKey style={{ color: '#667eea' }} /> Admin List
                </h2>

                {adminList.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    style={{
                      textAlign: 'center',
                      padding: '3rem 0',
                      color: '#a0aec0'
                    }}
                  >
                    No administrators found
                  </motion.div>
                ) : (
                  <motion.ul
                    style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: 0,
                      display: 'grid',
                      gap: '1rem'
                    }}
                  >
                    <AnimatePresence>
                      {adminList.map((admin) => (
                        <motion.li
                          key={admin.key}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, x: 50 }}
                          transition={{ type: "spring", stiffness: 300 }}
                          layout
                          style={{
                            padding: '1.2rem',
                            borderRadius: '12px',
                            background: 'rgba(255, 255, 255, 0.9)',
                            border: '1px solid #e2e8f0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <div>
                            <div style={{
                              fontWeight: '600',
                              marginBottom: '0.5rem',
                              fontSize: '1.1rem',
                              color: '#4a5568'
                            }}>
                              {admin.id}
                            </div>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              color: '#718096'
                            }}>
                              <span>Password:</span>
                              {showPassword[admin.key] ? (
                                <span>{admin.pass}</span>
                              ) : (
                                <span style={{ letterSpacing: '2px' }}>••••••••</span>
                              )}
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => togglePasswordVisibility(admin.key)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#667eea',
                                  cursor: 'pointer',
                                  padding: '0.2rem',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                {showPassword[admin.key] ? <FaEyeSlash /> : <FaEye />}
                              </motion.button>
                            </div>
                          </div>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleDeleteAdmin(admin.key)}
                            style={{
                              background: 'rgba(255, 107, 107, 0.1)',
                              border: '1px solid rgba(255, 107, 107, 0.3)',
                              color: '#e53e3e',
                              borderRadius: '8px',
                              padding: '0.5rem 0.8rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            <FaTrash /> Remove
                          </motion.button>
                        </motion.li>
                      ))}
                    </AnimatePresence>
                  </motion.ul>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

export default ManageAccess;